import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { resolveApiEndpointPath } from '../config/api-endpoints.config';

export const SCREEN_LOCKED_MESSAGE = 'The SLL screen is locked. Your attempted changes were not saved.';

export function isScreenLockedError(error: unknown): boolean {
  return hasApiErrorCode(error, ['SCREEN_LOCKED']);
}

export function isScreenLockDeniedError(error: unknown): boolean {
  return hasApiErrorCode(error, ['SCREEN_LOCKED', 'LOCK_STATUS_UNAVAILABLE']);
}

function hasApiErrorCode(error: unknown, codes: string[]): boolean {
  const accepted = new Set(codes.map((code) => code.trim().toUpperCase()));
  let candidate: unknown = error;
  for (let depth = 0; depth < 4; depth++) {
    if (!candidate || typeof candidate !== 'object') {
      return false;
    }
    const record = candidate as Record<string, unknown>;
    const code = record['code'] ?? record['errorCode'] ?? record['error_code'];
    if (typeof code === 'string' && accepted.has(code.trim().toUpperCase())) {
      return true;
    }
    candidate = record['error'] ?? record['responsePayload'] ?? record['details'];
  }
  return false;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  let candidate: unknown = error;
  for (let depth = 0; depth < 4; depth++) {
    if (!candidate || typeof candidate !== 'object') {
      return fallback;
    }
    const record = candidate as Record<string, unknown>;
    const nested = record['error'] ?? record['responsePayload'] ?? record['details'];
    if (nested !== null && typeof nested === 'object') {
      candidate = nested;
      continue;
    }
    for (const key of ['message', 'detail', 'errorMessage']) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    candidate = nested;
  }
  return fallback;
}

export interface ScreenLockStatus {
  screenCode: string;
  locked: boolean;
  lastUpdated: string | null;
}

@Injectable({ providedIn: 'root' })
export class ScreenLockService {
  private readonly http = inject(HttpClient);

  private unwrapPayload<T>(response: unknown): T {
    if (
      response !== null
      && typeof response === 'object'
      && 'responsePayload' in (response as Record<string, unknown>)
    ) {
      return (response as { responsePayload: T }).responsePayload;
    }
    return response as T;
  }

  private mapStatus(status: unknown): ScreenLockStatus {
    if (!isRecord(status) || typeof status['screenCode'] !== 'string' || typeof status['locked'] !== 'boolean') {
      throw new Error('The screen-lock service returned an invalid status.');
    }
    return {
      screenCode: status['screenCode'],
      locked: status['locked'],
      lastUpdated: typeof status['lastUpdated'] === 'string' ? status['lastUpdated'] : null,
    };
  }

  getStatuses(lenderId: number, finPrdCd: string): Observable<ScreenLockStatus[]> {
    const params = new HttpParams().set('lenderId', lenderId).set('finPrdCd', finPrdCd);
    return this.http
      .get<unknown>(resolveApiEndpointPath('get-screen-lock-status'), { params })
      .pipe(
        map((response) => {
          const statuses = this.unwrapPayload<unknown>(response);
          if (!Array.isArray(statuses)) {
            throw new Error('The screen-lock service returned an invalid status list.');
          }
          return statuses.map((status: unknown) => this.mapStatus(status));
        })
      );
  }

  getStatus(lenderId: number, finPrdCd: string, screenCode: string): Observable<ScreenLockStatus> {
    const params = new HttpParams().set('lenderId', lenderId).set('finPrdCd', finPrdCd);
    return this.http
      .get<unknown>(resolveApiEndpointPath('get-screen-lock-status-by-screen', { screenCode }), { params })
      .pipe(map((response) => this.mapStatus(this.unwrapPayload<unknown>(response))));
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}